import { useState, type FC } from 'react';
import { reasonForCallObj } from 'infrastructure/consts/device-troubleshooting-modal';

import AppendixCModal from './modals/appendix-c-modal';
import IntroCallModal from './modals/intro-call-modal';
import NormalReadingsModal from './modals/normal-readings-modal';
import DeviceTroubleshootingModal from './modals/device-troubleshooting-modal';
import NoReadingsModal from './modals/no-readings-modal';
import WrapUpModal from './modals/wrap-up-modal';
import MonthlyInteractionCall from './modals/monthly-interaction-call';

import type { IAppendixCValues, IDeviceTroubleshootModalsProps } from './types';

const DeviceTroubleshootModals: FC<IDeviceTroubleshootModalsProps> = (
  props,
) => {
  const {
    patientGuid,
    openAppendixCModal,
    toggleOpenAppendixCModal,
    openIntroCallModal,
    toggleOpenIntroCallModal,
    openNormalReadingsModal,
    toggleOpenNormalReadingsModal,
    openDeviceTroubleshootingModal,
    toggleOpenDeviceTroubleshootingModal,
    openNoReadingsModal,
    toggleOpenNoReadingsModal,
    openMonthlyInteractionCall,
    toggleOpenMonthlyInteractionCall,
    openWrapUpModal,
    toggleOpenWrapUpModal,
    onSaveForm,
    onAppointmentChange,
  } = props;
  const [modalList, setModalList] = useState<string[]>([]);
  const [whichPerson, setWhichPersonState] = useState<string>('');
  const [otherPerson, setOtherPersonState] = useState<string>('');

  const getAppendixCValues = () => {
    const storedValue = localStorage.getItem(
      `device-troubleshoot_appendixC_${patientGuid}`,
    );
    if (storedValue) {
      const parsedValue = JSON.parse(storedValue);
      const appendixCValues: IAppendixCValues = parsedValue;
      const { reasonForCall } = appendixCValues;
      return reasonForCall;
    }
    return [];
  };

  const openNextModal = (reasonForCall: string[], modalTitle?: string) => {
    if (modalTitle) {
      setModalList(modalList.filter((m) => m !== modalTitle));
    }
    if (reasonForCall.includes(reasonForCallObj.introductoryCall)) {
      toggleOpenIntroCallModal(true);
      return true;
    }
    if (reasonForCall.includes(reasonForCallObj.normalReadings)) {
      toggleOpenNormalReadingsModal(true);
      return true;
    }
    if (reasonForCall.includes(reasonForCallObj.deviceTroubleshoot)) {
      toggleOpenDeviceTroubleshootingModal(true);
      return true;
    }
    if (reasonForCall.includes(reasonForCallObj.noReadings)) {
      toggleOpenNoReadingsModal(true);
      return true;
    }
    if (reasonForCall.includes(reasonForCallObj.monthlyInteractionCall)) {
      toggleOpenMonthlyInteractionCall(true);
      return true;
    }

    if (reasonForCall.length === 0 || reasonForCall.includes('Other')) {
      toggleOpenWrapUpModal(true);
    }
  };
  const setWhichPerson = (newWhichPerson: string) => {
    const deviceTroubleshootKeys = Object.keys(localStorage).filter((key) =>
      key.startsWith('device-troubleshoot'),
    );

    deviceTroubleshootKeys.forEach((key) => {
      const storedValue = localStorage.getItem(key);
      if (storedValue) {
        const parsedValue = JSON.parse(storedValue);
        parsedValue.whichPerson = newWhichPerson;
        localStorage.setItem(key, JSON.stringify(parsedValue));
      }
    });

    setWhichPersonState(newWhichPerson);
  };

  const setOtherPerson = (newOtherPerson: string) => {
    const deviceTroubleshootKeys = Object.keys(localStorage).filter((key) =>
      key.startsWith('device-troubleshoot'),
    );

    deviceTroubleshootKeys.forEach((key) => {
      const storedValue = localStorage.getItem(key);
      if (storedValue) {
        const parsedValue = JSON.parse(storedValue);
        parsedValue.otherPerson = newOtherPerson;
        localStorage.setItem(key, JSON.stringify(parsedValue));
      }
    });

    setOtherPersonState(newOtherPerson);
  };
  return (
    <>
      {openAppendixCModal && (
        <AppendixCModal
          patientGuid={patientGuid}
          isOpen={openAppendixCModal}
          closeModal={() => toggleOpenAppendixCModal(false)}
          handleSubmit={(msg: string) => {
            onSaveForm(msg);
            const reasonForCall = getAppendixCValues();
            setModalList(reasonForCall.filter((m) => m !== 'Other'));
            openNextModal(reasonForCall);
          }}
          setWhichPerson={setWhichPerson}
          setOtherPerson={setOtherPerson}
        />
      )}
      {openIntroCallModal && (
        <IntroCallModal
          patientGuid={patientGuid}
          isOpen={openIntroCallModal}
          closeModal={() => toggleOpenIntroCallModal(false)}
          handleSubmit={(msg: string) => {
            onSaveForm(msg);
            openNextModal(
              modalList.filter((m) => m !== reasonForCallObj.introductoryCall),
              reasonForCallObj.introductoryCall,
            );
          }}
          whichPerson={whichPerson}
          otherPerson={otherPerson}
        />
      )}
      {openNormalReadingsModal && (
        <NormalReadingsModal
          patientGuid={patientGuid}
          isOpen={openNormalReadingsModal}
          closeModal={() => toggleOpenNormalReadingsModal(false)}
          handleSubmit={(msg: string) => {
            onSaveForm(msg);
            openNextModal(
              modalList.filter((m) => m !== reasonForCallObj.normalReadings),
              reasonForCallObj.normalReadings,
            );
          }}
          whichPerson={whichPerson}
          otherPerson={otherPerson}
        />
      )}
      {openDeviceTroubleshootingModal && (
        <DeviceTroubleshootingModal
          patientGuid={patientGuid}
          isOpen={openDeviceTroubleshootingModal}
          closeModal={() => toggleOpenDeviceTroubleshootingModal(false)}
          handleSubmit={(msg: string) => {
            onSaveForm(msg);
            openNextModal(
              modalList.filter(
                (m) => m !== reasonForCallObj.deviceTroubleshoot,
              ),
              reasonForCallObj.deviceTroubleshoot,
            );
          }}
          whichPerson={whichPerson}
          otherPerson={otherPerson}
        />
      )}
      {openNoReadingsModal && (
        <NoReadingsModal
          isOpen={openNoReadingsModal}
          closeModal={() => toggleOpenNoReadingsModal(false)}
          handleSubmit={(msg: string) => {
            onSaveForm(msg);
            openNextModal(
              modalList.filter((m) => m !== reasonForCallObj.noReadings),
              reasonForCallObj.noReadings,
            );
          }}
          whichPerson={whichPerson}
          otherPerson={otherPerson}
          patientGuid={patientGuid}
        />
      )}
      {openMonthlyInteractionCall && (
        <MonthlyInteractionCall
          patientGuid={patientGuid}
          isOpen={openMonthlyInteractionCall}
          closeModal={() => toggleOpenMonthlyInteractionCall(false)}
          handleSubmit={(msg: string) => {
            onSaveForm(msg);
            openNextModal(
              modalList.filter(
                (m) => m !== reasonForCallObj.monthlyInteractionCall,
              ),
              reasonForCallObj.monthlyInteractionCall,
            );
          }}
          toggleOpenWrapUpModal={toggleOpenWrapUpModal}
          toggleOpenMonthlyInteractionCall={toggleOpenMonthlyInteractionCall}
          whichPerson={whichPerson}
          otherPerson={otherPerson}
          onAppointmentChange={onAppointmentChange}
        />
      )}
      {openWrapUpModal && (
        <WrapUpModal
          patientGuid={patientGuid}
          isOpen={openWrapUpModal}
          closeModal={() => toggleOpenWrapUpModal(false)}
          handleSubmit={(msg: string) => {
            onSaveForm(msg);
            toggleOpenWrapUpModal(false);
          }}
          whichPerson={whichPerson}
          otherPerson={otherPerson}
        />
      )}
    </>
  );
};

export default DeviceTroubleshootModals;
